// ============================================================================
// Home and My Work — the shape of a row, and the two rules the phone applies.
//
// Item #650. Everything except project cards comes from get_home_work(), which
// already says which band a row is in (the database decides, TCSD-0341).
// Project cards are read through the project gate — getAdvanceBlockers() is the
// one copy of "what a card still needs before it can go to finance" — so a card
// the gate blocks is Overdue (Ben, 2026-09-30: project details needed for
// invoicing are things that MUST be done) and a complete card is Today: it only
// needs sending.
// ============================================================================
import type { ProjectRow } from '../../shared/types';
import { getAdvanceBlockers } from '../project-work/lib/advance-gate';

export type Band = 'overdue' | 'today' | 'this_week';
export type Kind = 'Project' | 'Complaint' | 'New account' | 'Admin';

export interface WorkRow {
  /** Stable key: item_type + ':' + item_id */
  key: string;
  itemType: string;
  itemId: string;
  kind: Kind;
  facilityId: string | null;
  /** The building with its main cleaner, or the record's own title. */
  name: string;
  context: string;
  /** What the row is asking for. Read from the record, never typed here. */
  missing: string;
  /** True when nothing is missing and the row only needs an action (send to Jose). */
  ready: boolean;
  ageDays: number | null;
  band: Band;
  grp: number;
  link: string;
  focusKey: string | null;
  amEid: string | null;
  amName: string | null;
  entityType: string | null;
  entityId: string | null;
  canHandOff: boolean;
  billAmount: number | null;
}

export interface HomeWorkDbRow {
  item_type: string;
  item_id: string;
  kind: string;
  facility_id: string | null;
  facility_label: string | null;
  title: string | null;
  context: string | null;
  missing: string | null;
  age_days: number | null;
  band: string;
  grp: number;
  deep_link: string | null;
  focus_key: string | null;
  am_eid: string | null;
  am_name: string | null;
  entity_type: string | null;
  entity_id: string | null;
  can_hand_off: boolean;
}

export function fromDb(r: HomeWorkDbRow): WorkRow {
  return {
    key: `${r.item_type}:${r.item_id}`,
    itemType: r.item_type,
    itemId: r.item_id,
    kind: (['Project', 'Complaint', 'New account', 'Admin'].includes(r.kind) ? r.kind : 'Admin') as Kind,
    facilityId: r.facility_id,
    name: r.facility_label || r.title || 'Untitled',
    context: r.facility_label && r.title ? r.title : (r.context ?? ''),
    missing: r.missing ?? '',
    ready: false,
    ageDays: r.age_days,
    band: (r.band === 'overdue' || r.band === 'today' ? r.band : 'this_week') as Band,
    grp: r.grp,
    link: r.deep_link || '/my-work',
    focusKey: r.focus_key,
    amEid: r.am_eid,
    amName: r.am_name,
    entityType: r.entity_type,
    entityId: r.entity_id,
    canHandOff: r.can_hand_off,
    billAmount: null,
  };
}

/** The dismissal key the database already uses for a not-sent card (get_my_work). */
export const PROJECT_ITEM_TYPE = 'project_work_unsent';
export const projectItemId = (projectId: string) => `UNSENT:${projectId}`;

function daysSince(iso: string | null | undefined, today: Date): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((today.getTime() - d.getTime()) / 86_400_000));
}

function workWords(p: ProjectRow): string {
  const t = (p as { title?: string | null }).title;
  if (t && t.trim()) return t.trim();
  const line = p.project_services?.find((l) => l.service_catalog?.display_name || l.scope_text);
  return line?.service_catalog?.display_name || line?.scope_text || p.service_type_raw || 'Project work';
}

const money = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

export function projectRow(p: ProjectRow, mainCleaner: Map<string, string>, myEid: string | null, today = new Date()): WorkRow {
  const blockers = getAdvanceBlockers(p);
  const facName = p.facilities?.facility_name ?? p.facility_name ?? null;
  const am = p.facilities?.account_manager_eid ?? null;
  const amName = (() => {
    const a = (p.facilities as { account_manager?: { employee_name_first: string | null; employee_name_last: string | null } | null } | null)?.account_manager;
    return a ? `${a.employee_name_first ?? ''} ${a.employee_name_last ?? ''}`.trim() || null : null;
  })();
  const name = p.facility_id && facName
    ? `${facName} (${mainCleaner.get(p.facility_id) ?? 'no main cleaner on file'})`
    : 'No building on the card';
  const creator = (p as { creator?: { employee_name_first: string | null; employee_name_last: string | null } | null }).creator;
  const by = creator ? `${creator.employee_name_first ?? ''} ${creator.employee_name_last ?? ''}`.trim() : '';
  const parts = [workWords(p)];
  if (p.bill_amount != null) parts.push(`bill ${money(Number(p.bill_amount))}`);
  if (by) parts.push(`by ${by}`);
  return {
    key: `${PROJECT_ITEM_TYPE}:${projectItemId(p.project_id)}`,
    itemType: PROJECT_ITEM_TYPE,
    itemId: projectItemId(p.project_id),
    kind: 'Project',
    facilityId: p.facility_id ?? null,
    name,
    context: parts.join(' · '),
    missing: blockers.length > 0 ? blockers.join(' · ') : 'Ready — send to Jose',
    ready: blockers.length === 0,
    ageDays: daysSince(p.created_at, today),
    band: blockers.length > 0 ? 'overdue' : 'today',
    grp: 2,
    link: `/project-work/${p.project_id}`,
    focusKey: blockers.length > 0 ? firstProjectFocus(p) : null,
    amEid: am,
    amName,
    entityType: 'project',
    entityId: p.project_id,
    canHandOff: !!am && am !== myEid,
    billAmount: p.bill_amount != null ? Number(p.bill_amount) : null,
  };
}

/** Which field on the card to land on: the first thing the gate is asking for. */
function firstProjectFocus(p: ProjectRow): string {
  if ((p.project_services ?? []).length > 0) return 'project:lines';
  if (p.bill_amount == null) return 'project:bill_amount';
  if (p.pay_amount == null) return 'project:pay_amount';
  return 'project:completion_date';
}

/**
 * Overdue: start and end dates first, then the building gaps, then project cards,
 * oldest first inside each. Today and This week: oldest first. (Final draft,
 * 2026-09-30.)
 */
export function sortBand(rows: WorkRow[]): WorkRow[] {
  return [...rows].sort((a, b) => a.grp - b.grp || (b.ageDays ?? -1) - (a.ageDays ?? -1));
}

export function byBand(rows: WorkRow[]): Record<Band, WorkRow[]> {
  const out: Record<Band, WorkRow[]> = { overdue: [], today: [], this_week: [] };
  for (const r of rows) out[r.band].push(r);
  return { overdue: sortBand(out.overdue), today: sortBand(out.today), this_week: sortBand(out.this_week) };
}

/**
 * The element a row opens at. Building cards already carry these anchors
 * (info-card-*); project cards carry work-focus-* on their fields.
 */
export function focusElementId(focusKey: string | null): string | null {
  if (!focusKey) return null;
  switch (focusKey) {
    case 'subcontractor':
    case 'sub_payment':
      return 'info-card-crew-and-sub';
    case 'client_rate':
    case 'final_pricing':
      return 'info-card-money';
    case 'am_eid':
    case 'start_date':
    case 'location':
      return 'info-card-account-summary';
    case 'facility_poc':
      return 'info-card-contacts';
    case 'sub_service_agreement':
      return 'info-card-documents';
    case 'project:lines':
      return 'work-focus-lines';
    case 'project:bill_amount':
      return 'work-focus-bill_amount';
    case 'project:pay_amount':
      return 'work-focus-pay_amount';
    case 'project:completion_date':
      return 'work-focus-completion_date';
    default:
      return null;
  }
}

/**
 * Open the card at the field. The card is not changed to do this — no hook is
 * added to it (TCSD-0271): after navigation we wait for the element to appear,
 * centre it, and put the cursor in its first input. `window.scrollTo` never
 * worked in this shell; the element scrolls itself into view.
 */
export function focusWhenReady(
  elementId: string | null,
  timeoutMs = 6000,
  // 'center' for a field (Home's rows). A whole section taller than the screen
  // lands at 'start', or its heading is scrolled off the top (item 833).
  block: ScrollLogicalPosition = 'center',
): void {
  if (!elementId || typeof window === 'undefined') return;
  const started = Date.now();
  // The card renders in stages (the building, then its lines, then its money),
  // so the target exists before it has settled. Scrolling the moment it appears
  // left the page at the top once the sections above it filled in. Wait until
  // its position has held still for a few ticks.
  let lastTop: number | null = null;
  let steady = 0;
  const tick = () => {
    const el = document.getElementById(elementId);
    if (el) {
      const top = el.getBoundingClientRect().top + (document.scrollingElement?.scrollTop ?? 0);
      steady = lastTop !== null && Math.abs(top - lastTop) < 1 ? steady + 1 : 0;
      lastTop = top;
      if (steady < 3 && Date.now() - started < timeoutMs) {
        window.setTimeout(tick, 150);
        return;
      }
      el.scrollIntoView({ block }); // instant: a smooth scroll is dropped when the tab is not painting
      const input = (el.matches('input,textarea,select') ? el : el.querySelector('input,textarea,select')) as HTMLElement | null;
      input?.focus({ preventScroll: true });
      el.setAttribute('data-work-focused', 'true');
      window.setTimeout(() => el.removeAttribute('data-work-focused'), 2500);
      return;
    }
    if (Date.now() - started < timeoutMs) window.setTimeout(tick, 120);
  };
  window.setTimeout(tick, 60);
}

/** Kansas City day, the day the daily summaries already use (TCSD-0351). */
export function kcDay(d = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago' }).format(d);
}
